import { Button } from "@/components/ui/button"
import { BaseEdge, EdgeLabelRenderer, EdgeProps, getBezierPath, useReactFlow } from "@xyflow/react"
import { X, Wrench, ChevronDown, ChevronRight } from "lucide-react"
import { app } from "@/lib/app"
import { DisplayContent } from "./nodes/displaytypes"
import { useEffect, useState } from "react"


// Animated edge component for running workers
function AnimatedEdge({ path, isRunning }: { path: string; isRunning: boolean }) {
  const [dashOffset, setDashOffset] = useState(0)

  useEffect(() => {
    if (!isRunning) return

    const interval = setInterval(() => {
      setDashOffset((prev) => (prev + 1) % 20)
    }, 50) // Faster animation for better visibility

    return () => clearInterval(interval)
  }, [isRunning])

  if (!isRunning) {
    return <BaseEdge path={path} style={{}} />
  }

  return (
    <>
      {/* Base edge */}
      <BaseEdge path={path} style={{}} />
      {/* Animated highlight edge */}
      <BaseEdge
        path={path}
        style={{
          stroke: '#8B5CF6', // Bright purple
          strokeWidth: 6, // Increased width for better visibility
          strokeDasharray: '10,10',
          strokeDashoffset: dashOffset,
        }}
      />
      {/* Glow effect */}
      <BaseEdge
        path={path}
        style={{
          stroke: '#A855F7', // Lighter purple for glow
          strokeWidth: 12, // Increased width for better glow effect
        }}
      />
    </>
  )
}

function ToolCallsDisplay({ toolCalls }: { toolCalls: { name: string; arguments: string; result: string }[] }) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set())

  function toggle(index: number) {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  return (
    <div className="bg-amber-50 p-2 text-xs border border-solid border-amber-300 shadow-md rounded-sm max-w-72 max-h-80 overflow-auto space-y-1">
      {toolCalls.map((tc, i) => {
        const isOpen = expanded.has(i)
        return (
          <div key={i}>
            <button
              onClick={() => toggle(i)}
              className="flex items-center gap-1.5 w-full text-left hover:bg-amber-100 rounded px-1 py-0.5 transition-colors"
            >
              <Wrench className="h-3 w-3 text-amber-600 shrink-0" />
              <span className="font-medium text-amber-800 truncate grow">{tc.name}</span>
              {isOpen
                ? <ChevronDown className="h-3 w-3 text-amber-500 shrink-0" />
                : <ChevronRight className="h-3 w-3 text-amber-500 shrink-0" />
              }
            </button>
            {isOpen && (
              <div className="ml-5 space-y-1 mt-1">
                <pre className="p-1.5 bg-white rounded border border-amber-200 text-gray-700 overflow-auto whitespace-pre-wrap break-all max-h-32 text-[11px] select-text cursor-text">
                  {(() => { try { return JSON.stringify(JSON.parse(tc.arguments), null, 2) } catch { return tc.arguments } })()}
                </pre>
                {tc.result && (
                  <pre className="p-1.5 bg-green-50 rounded border border-green-200 text-gray-700 overflow-auto whitespace-pre-wrap break-all max-h-40 text-[11px] select-text cursor-text">
                    {(() => { try { return JSON.stringify(JSON.parse(tc.result), null, 2) } catch { return tc.result } })()}
                  </pre>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export function ButtonEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, animated, source, target, sourceHandleId }: EdgeProps) {

  const [edgePath, labelX, labelY] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, })
  const { setEdges, getEdges } = useReactFlow()

  const onEdgeClick = () => {
    setEdges((edges) => edges.filter((edge) => edge.id !== id))
  }

  const worker = app.agent.workers[source]
  const { currentWorker } = app.agent

  // Check if this edge is part of the current execution path
  const isRunning = Boolean(currentWorker && currentWorker.id && (
    currentWorker.id === source ||
    currentWorker.id === target ||
    // Check if this edge connects to the current worker
    (currentWorker.id === source && app.agent.workers[target]) ||
    (currentWorker.id === target && app.agent.workers[source])
  ))

  let v = null
  let type: IOTypes = "unknown"
  let handleType: string | null = null

  if (worker) {
    const handle = worker.handles[sourceHandleId]
    if (handle) {
      v = handle.value
      type = handle.type
      handleType = handle.type
    }
  }

  if (!app.agent.displayData) type = "unknown"

  const targetWorker = app.agent.workers[target]
  const normalizeToolName = (s: string) => (s || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 64)
  const rawTargetToolName = targetWorker?.parameters?.toolName || targetWorker?.parameters?.toolDescription
  const targetToolName = normalizeToolName(rawTargetToolName)
  const toolCalls = app.agent.displayData && handleType === "tool" && targetWorker
    ? (app.agent.toolCallNodes || []).filter((tc: any) =>
        tc.sourceWorkerId === source && normalizeToolName(tc.name) === targetToolName
      )
    : []

  return <>
    <AnimatedEdge path={edgePath} isRunning={isRunning} />
    <EdgeLabelRenderer>
      <div className="nodrag nopan pointer-events-auto absolute" style={{ transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`, }}>
        {type === "unknown" && toolCalls.length === 0 && <Button onClick={onEdgeClick} size="icon" variant="secondary" className="rounded-full border border-solid border-gray-300 shadow-md w-6 h-6 bg-gray-400!">
          <X size={16} />
        </Button>}

        {app.agent.displayData && handleType !== "tool" && <>
          <DisplayContent
            type={type}
            value={v}
            className="bg-neutral-50 p-2 text-sm border border-solid border-gray-400 shadow-md rounded-sm max-w-64 max-h-64 overflow-auto"
          />
        </>}

        {toolCalls.length > 0 && <ToolCallsDisplay toolCalls={toolCalls} />}
      </div>
    </EdgeLabelRenderer>
  </>

}

